import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { LearnWorkbenchShell } from "../components/layout/LearnWorkbenchShell";
import { PracticeSurface } from "../components/practice/PracticeSurface";
import {
  NOTAS_CON_ALTERACION,
  buildNoteRoute,
  buildRoute,
  getAllPositions,
  getOctaveLabel,
  STRING_NAMES,
  type NoteRoute,
  type NavCoord,
  pitchClassToSpanishName,
  noteNameToPitchClass,
} from "../lib/noteNavigator";
import {
  noteToSemitones,
  semitonesToNote,
  shouldUseFlats,
  type NoteName,
  type NeckPosition,
} from "../lib/guitarEngine";
import { getInstrumentConfig } from "../lib/instrumentEngine";
import { useInstrument } from "../contexts/InstrumentContext";
import { useSpaciousWorkbench } from "../lib/useSpaciousWorkbench";
import { usePracticeMode } from "../contexts/PracticeModeContext";
import { useAudioEngine } from "../hooks/useAudioEngine";
import { useGuitarSynth } from "../hooks/useGuitarSynth";
import { useStudentFavorites } from "../hooks/useStudentFavorites";
import type { NeckConnection } from "../components/practice/PracticeSurface";
import { HelpTooltip } from "../components/ui/HelpTooltip";
import { Map, Bookmark } from "lucide-react";
import { PEDAGOGICAL_DEGREE_HEX } from "../lib/scaleModuleTheme";

const DEGREE_DEFS = [
  { num: 1, name: "Tónica", interval: 0, roman: "I" },
  { num: 2, name: "Supertonica", interval: 2, roman: "II" },
  { num: 3, name: "Mediante", interval: 4, roman: "III" },
  { num: 4, name: "Subdominante", interval: 5, roman: "IV" },
  { num: 5, name: "Dominante", interval: 7, roman: "V" },
  { num: 6, name: "Submediante", interval: 9, roman: "VI" },
  { num: 7, name: "Sensible", interval: 11, roman: "VII" },
] as const;

const DEGREE_INFO = DEGREE_DEFS.map((d, i) => ({
  ...d,
  color: PEDAGOGICAL_DEGREE_HEX[i],
}));

const ROUTE_COLORS = [...PEDAGOGICAL_DEGREE_HEX];

// ─── Helpers ───
function noteToSpanish(note: NoteName): string {
  const map: Record<string, string> = {
    C: "Do", "C#": "Do#", Db: "Reb",
    D: "Re", "D#": "Re#", Eb: "Mib",
    E: "Mi", F: "Fa", "F#": "Fa#", Gb: "Solb",
    G: "Sol", "G#": "Sol#", Ab: "Lab",
    A: "La", "A#": "La#", Bb: "Sib",
    B: "Si",
  };
  return map[note] || note;
}

function getDegreeNote(tonic: NoteName, degreeNum: number): NoteName {
  const info = DEGREE_INFO.find((d) => d.num === degreeNum);
  if (!info) return tonic;
  const rootSemi = noteToSemitones(tonic);
  const useFlats = shouldUseFlats(tonic, "major");
  return semitonesToNote(rootSemi + info.interval, useFlats);
}

function getTriadName(degrees: number[]): string | null {
  const sorted = [...degrees].sort((a, b) => a - b);
  const key = sorted.join(",");
  const triads: Record<string, string> = {
    "1,3,5": "Acorde Mayor (I)",
    "2,4,6": "Acorde menor (II)",
    "3,5,7": "Acorde menor (III)",
    "1,4,6": "Acorde Mayor (IV)",
    "2,5,7": "Acorde Mayor (V)",
    "1,3,6": "Acorde menor (VI)",
    "2,4,7": "Acorde disminuido (VII°)",
  };
  return triads[key] || null;
}

export const NotasExplorerPage: React.FC = () => {
  const { instrument } = useInstrument();
  const stringCount = getInstrumentConfig(instrument).strings.length;

  // ─── Vista global ───
  const [viewMode, setViewMode] = useState<"octavas" | "grados">("octavas");
  const [stringOrder, setStringOrder] = useState<"tab" | "physical">("physical");
  const [leftHanded, setLeftHanded] = useState(false);

  // ─── Estado modo Octavas ───
  const [selectedNote, setSelectedNote] = useState<string>("C");
  const [showRoute, setShowRoute] = useState(true);
  const [anchorOverride, setAnchorOverride] = useState<NavCoord | null>(null);

  // ─── Estado modo Grados ───
  const [tonic, setTonic] = useState<NoteName>("C");
  const [selectedDegrees, setSelectedDegrees] = useState<number[]>([1]);
  const [showDegreeRoutes, setShowDegreeRoutes] = useState(false);
  const [routeForDegrees, setRouteForDegrees] = useState<number[]>([]);

  // ─── Práctica + sintetizador ───
  const { mode: practiceMode } = usePracticeMode();
  const { playFret } = useGuitarSynth();
  const { toggleFavorite, isFavorite } = useStudentFavorites();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSeqIndex, setCurrentSeqIndex] = useState(0);
  const [activeNote, setActiveNote] = useState<{ string: number; fret: number } | null>(null);

  const noteLabel = useMemo(() => {
    const entry = NOTAS_CON_ALTERACION.find((n) => n.value === selectedNote);
    return entry?.label || selectedNote;
  }, [selectedNote]);

  // ─── Modo Octavas: lógica ───
  const allPositions = useMemo(() => getAllPositions(selectedNote, 15, instrument), [selectedNote, instrument]);

  const route: NoteRoute | null = useMemo(() => {
    if (anchorOverride) return buildRoute(anchorOverride.string, anchorOverride.fret, 15, instrument);
    return buildNoteRoute(selectedNote, 15, instrument);
  }, [selectedNote, anchorOverride]);

  const octavaNeckPositions: NeckPosition[] = useMemo(() => {
    if (!showRoute) {
      return allPositions.map((p) => ({
        string: p.string, fret: p.fret, note: p.note as NoteName, highlight: "scale" as const,
      }));
    }
    const routeCoords = route?.allCoords || [];
    const routeKeys = new Set(routeCoords.map((c) => `${c.string}-${c.fret}`));
    return allPositions.map((p) => {
      const isRoute = routeKeys.has(`${p.string}-${p.fret}`);
      const isAnchor = route?.anchor.string === p.string && route?.anchor.fret === p.fret;
      return {
        string: p.string, fret: p.fret, note: p.note as NoteName,
        highlight: isAnchor ? "root" : isRoute ? "active" : "scale",
      };
    });
  }, [allPositions, route, showRoute]);

  const octavaConnections: NeckConnection[] = useMemo(() => {
    if (!showRoute || !route) return [];
    return route.steps.map((step, i) => ({
      from: { string: step.from.string, fret: step.from.fret },
      to: { string: step.to.string, fret: step.to.fret },
      color: ROUTE_COLORS[i % ROUTE_COLORS.length],
      label: `${step.arrow} ${step.label}`,
      arrow: true,
    }));
  }, [route, showRoute]);

  const handleOctavaFretClick = (stringNum: number, fret: number, _note: NoteName) => {
    if (!showRoute) return;
    const pos = allPositions.find((p) => p.string === stringNum && p.fret === fret);
    if (pos) setAnchorOverride(pos);
  };

  // ─── Modo Grados: lógica ───
  const degreeData = useMemo(() => {
    return DEGREE_INFO.map((d) => ({
      ...d,
      note: getDegreeNote(tonic, d.num),
      noteSpanish: noteToSpanish(getDegreeNote(tonic, d.num)),
    }));
  }, [tonic]);

  const degreeNeckPositions: NeckPosition[] = useMemo(() => {
    const positions: NeckPosition[] = [];
    for (const degNum of selectedDegrees) {
      const info = degreeData.find((d) => d.num === degNum);
      if (!info) continue;
      const noteName = info.note;
      const all = getAllPositions(noteName, 15, instrument);
      for (const p of all) {
        positions.push({
          string: p.string,
          fret: p.fret,
          note: p.note as NoteName,
          degree: info.roman as any,
          highlight: degNum === 1 ? "root" : degNum === 5 ? "fifth" : degNum === 3 ? "third" : "scale",
        });
      }
    }
    return positions;
  }, [degreeData, selectedDegrees]);

  const degreeConnections: NeckConnection[] = useMemo(() => {
    if (!showDegreeRoutes) return [];
    const conns: NeckConnection[] = [];
    for (const degNum of routeForDegrees) {
      const info = degreeData.find((d) => d.num === degNum);
      if (!info) continue;
      const noteRoute = buildNoteRoute(info.note, 15, instrument);
      if (!noteRoute) continue;
      noteRoute.steps.forEach((step, i) => {
        conns.push({
          from: { string: step.from.string, fret: step.from.fret },
          to: { string: step.to.string, fret: step.to.fret },
          color: info.color,
          label: i === 0 ? `${info.roman}` : undefined,
          arrow: true,
          dashed: true,
        });
      });
    }
    return conns;
  }, [degreeData, showDegreeRoutes, routeForDegrees]);

  const toggleDegree = (num: number) => {
    setSelectedDegrees((prev) =>
      prev.includes(num) ? prev.filter((d) => d !== num) : [...prev, num]
    );
  };

  const toggleRouteForDegree = (num: number) => {
    setRouteForDegrees((prev) =>
      prev.includes(num) ? prev.filter((d) => d !== num) : [...prev, num]
    );
  };

  // ─── Secuencia de práctica ───
  const sequence = useMemo(() => {
    if (viewMode === "octavas") {
      return allPositions.map((p) => ({
        string: p.string,
        fret: p.fret,
        note: p.note as NoteName,
        highlight: "scale" as const,
      }));
    }
    return degreeNeckPositions;
  }, [viewMode, allPositions, degreeNeckPositions]);

  const sequenceRef = useRef(sequence);
  useEffect(() => { sequenceRef.current = sequence; }, [sequence]);

  const currentSeqIndexRef = useRef(currentSeqIndex);
  useEffect(() => { currentSeqIndexRef.current = currentSeqIndex; }, [currentSeqIndex]);

  const { state: engineState, init, detectNote } = useAudioEngine();

  useEffect(() => {
    if (!engineState.isReady && !engineState.isLoading) {
      init();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isPlaying || sequence.length === 0) return;

    if (practiceMode === "practice") {
      setCurrentSeqIndex(0);
      setActiveNote({ string: sequence[0].string, fret: sequence[0].fret });
      let cancelled = false;
      const loop = async () => {
        while (!cancelled && isPlayingRef.current) {
          const detected = await detectNote(1500);
          if (cancelled || !isPlayingRef.current) break;
          const seq = sequenceRef.current;
          const idx = currentSeqIndexRef.current;
          if (!seq.length || idx >= seq.length) break;
          const target = seq[idx].note;
          if (detected && detected.note === target) {
            const next = idx + 1;
            if (next >= seq.length) {
              setIsPlaying(false);
              setCurrentSeqIndex(0);
              setActiveNote(null);
            } else {
              setCurrentSeqIndex(next);
              setActiveNote({ string: seq[next].string, fret: seq[next].fret });
            }
          }
        }
      };
      loop();
      return () => { cancelled = true; };
    }

    const intervalMs = 800;
    let idx = 0;
    const timer = setInterval(() => {
      if (idx >= sequence.length) {
        setIsPlaying(false);
        setCurrentSeqIndex(0);
        setActiveNote(null);
        return;
      }
      const pos = sequence[idx];
      setActiveNote({ string: pos.string, fret: pos.fret });
      setCurrentSeqIndex(idx);
      idx++;
    }, intervalMs);
    return () => clearInterval(timer);
  }, [isPlaying, sequence, practiceMode, detectNote]);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentSeqIndex(0);
    setActiveNote(null);
  }, [viewMode, selectedNote, tonic, selectedDegrees]);

  const handleFretClick = (stringNum: number, fret: number, note: NoteName) => {
    playFret(stringNum, fret);
    if (viewMode === "octavas") {
      handleOctavaFretClick(stringNum, fret, note);
    }
  };

  // ─── Sidebar ───
  const sidebar = (
    <div className="space-y-4">
      {/* Toggle de vista */}
      <div>
        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5 block">
          Modo
        </label>
        <div className="flex gap-1">
          <button
            onClick={() => setViewMode("octavas")}
            className={`flex-1 h-9 rounded-md text-xs font-sans font-medium transition-all ${
              viewMode === "octavas"
                ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
            }`}
          >
            🗺️ Octavas
          </button>
          <button
            onClick={() => setViewMode("grados")}
            className={`flex-1 h-9 rounded-md text-xs font-sans font-medium transition-all ${
              viewMode === "grados"
                ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
            }`}
          >
            🎹 Grados
          </button>
        </div>
      </div>

      <button
        onClick={() => {
          const title = viewMode === "octavas"
            ? `Nota ${selectedNote}`
            : `Grados de ${tonic}`;
          toggleFavorite({
            type: "pattern",
            title,
            subtitle: viewMode === "octavas" ? "Modo Octavas" : "Modo Grados",
            url: "/notas",
          });
        }}
        className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-sans font-medium transition-all ${
          isFavorite("/notas")
            ? "bg-[var(--bordona-accent-soft)] text-[var(--bordona-accent)] border border-[var(--bordona-accent-border)]"
            : "bg-white/[0.08] text-slate-400 border border-white/10 hover:bg-white/10 hover:text-slate-200"
        }`}
        style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
      >
        <Bookmark className={`w-3.5 h-3.5 ${isFavorite("/notas") ? "fill-current" : ""}`} />
        {isFavorite("/notas") ? "Guardado" : "Guardar nota"}
      </button>

      {viewMode === "octavas" ? (
        <>
          {/* Selector de nota (octavas) */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5 block">
              Nota
            </label>
            <div className="grid grid-cols-3 gap-1">
              {NOTAS_CON_ALTERACION.map((n) => (
                <button
                  key={n.value}
                  onClick={() => { setSelectedNote(n.value); setAnchorOverride(null); }}
                  className={`h-9 rounded-md text-xs font-sans font-bold transition-all ${
                    selectedNote === n.value
                      ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                      : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
                  }`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </div>

          {/* Modo visualización */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5 block">
              Visualización
            </label>
            <div className="flex gap-1">
              <button
                onClick={() => setShowRoute(true)}
                className={`flex-1 h-9 rounded-md text-xs font-sans font-medium transition-all ${
                  showRoute ? "bg-[#2C2825] text-white" : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
                }`}
              >
                Ruta
              </button>
              <button
                onClick={() => setShowRoute(false)}
                className={`flex-1 h-9 rounded-md text-xs font-sans font-medium transition-all ${
                  !showRoute ? "bg-[#2C2825] text-white" : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
                }`}
              >
                Todas
              </button>
            </div>
          </div>

          {/* Info de anclaje */}
          {route && showRoute && (
            <div className="p-3 rounded-lg bg-white/[0.07] border border-white/10">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1">
                Anclaje
              </div>
              <div className="text-sm font-sans text-slate-100">
                Cuerda {route.anchor.string} ({STRING_NAMES[route.anchor.string]}) · Traste {route.anchor.fret}
              </div>
              <div className="text-xs text-slate-400 font-sans mt-0.5">
                {getOctaveLabel(route.anchor.midi, route.anchor.note)}
              </div>
              {anchorOverride && (
                <button
                  onClick={() => setAnchorOverride(null)}
                  className="mt-2 text-[10px] text-[var(--bordona-accent)] font-semibold underline"
                >
                  Restablecer anclaje
                </button>
              )}
              {!anchorOverride && (
                <div className="mt-2 text-[10px] text-slate-400 font-sans italic">
                  Toca cualquier {noteLabel} en el mástil para anclar desde ahí.
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Selector de tónica (grados) */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5 block">
              Tónica / Escala Mayor de...
            </label>
            <div className="grid grid-cols-4 gap-1">
              {NOTAS_CON_ALTERACION.map((n) => (
                <button
                  key={n.value}
                  onClick={() => setTonic(n.value as NoteName)}
                  className={`h-9 rounded-md text-xs font-sans font-bold transition-all ${
                    tonic === n.value
                      ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                      : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
                  }`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </div>

          {/* Grados de la escala */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5 block flex items-center gap-1.5">
              Grados a visualizar
              <HelpTooltip term="Grados" explanation="Los grados son los nombres funcionales de cada nota en una escala. I = Tónica (reposo), II = Supertonica, III = Mediante, IV = Subdominante, V = Dominante (tension), VI = Submediante, VII = Sensitiva (tension maxima)." example="En Do Mayor: I=Do, III=Mi, V=Sol. El acorde I-III-V forma un acorde mayor." />
            </label>
            <div className="space-y-1">
              {degreeData.map((d) => (
                <div key={d.num} className="flex items-center gap-2">
                  <button
                    onClick={() => toggleDegree(d.num)}
                    className={`flex-1 flex items-center gap-2 p-2 rounded-lg text-left transition-all border ${
                      selectedDegrees.includes(d.num)
                        ? "bg-white/[0.08] border-[var(--bordona-accent)] shadow-sm"
                        : "bg-white/[0.07] border-transparent hover:bg-white/10"
                    }`}
                  >
                    <span
                      className="w-4 h-4 rounded-full shrink-0"
                      style={{ backgroundColor: d.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-sans font-semibold text-slate-100">
                        {d.roman} — {d.name}
                      </div>
                      <div className="text-[10px] text-slate-400 font-sans">
                        {d.noteSpanish}
                      </div>
                    </div>
                    {selectedDegrees.includes(d.num) && (
                      <span className="text-[var(--bordona-accent)] text-xs font-bold">✓</span>
                    )}
                  </button>
                  {/* Toggle ruta mnemotécnica para este grado */}
                  <button
                    onClick={() => toggleRouteForDegree(d.num)}
                    disabled={!selectedDegrees.includes(d.num)}
                    className={`shrink-0 w-8 h-8 rounded-md text-[10px] font-bold transition-all ${
                      routeForDegrees.includes(d.num)
                        ? "bg-[var(--bordona-accent)] text-white"
                        : "bg-white/[0.07] text-slate-400 hover:bg-white/10"
                    } ${!selectedDegrees.includes(d.num) ? "opacity-30 cursor-not-allowed" : ""}`}
                    title="Mostrar ruta mnemotécnica"
                  >
                    <Map className="w-3.5 h-3.5 mx-auto" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Toggle rutas generales */}
          <div className="flex items-center gap-2">
            <input
              id="show-routes"
              type="checkbox"
              checked={showDegreeRoutes}
              onChange={(e) => setShowDegreeRoutes(e.target.checked)}
              className="w-4 h-4 accent-[var(--bordona-accent)]"
            />
            <label htmlFor="show-routes" className="text-xs font-sans text-slate-100 cursor-pointer">
              Mostrar rutas mnemotécnicas
            </label>
          </div>

          {/* Presets rápidos */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5 block">
              Presets
            </label>
            <div className="grid grid-cols-2 gap-1">
              <button
                onClick={() => { setSelectedDegrees([1, 3, 5]); setRouteForDegrees([]); }}
                className="px-2 py-1.5 rounded text-[10px] font-sans font-medium bg-white/[0.07] text-slate-300 hover:bg-white/10 transition-all"
              >
                Acorde I (Mayor)
              </button>
              <button
                onClick={() => { setSelectedDegrees([1, 2, 3, 4, 5, 6, 7]); setRouteForDegrees([]); }}
                className="px-2 py-1.5 rounded text-[10px] font-sans font-medium bg-white/[0.07] text-slate-300 hover:bg-white/10 transition-all"
              >
                Escala completa
              </button>
              <button
                onClick={() => { setSelectedDegrees([4, 5, 1]); setRouteForDegrees([]); }}
                className="px-2 py-1.5 rounded text-[10px] font-sans font-medium bg-white/[0.07] text-slate-300 hover:bg-white/10 transition-all"
              >
                Progresión IV-V-I
              </button>
              <button
                onClick={() => { setSelectedDegrees([5, 4, 1]); setRouteForDegrees([]); }}
                className="px-2 py-1.5 rounded text-[10px] font-sans font-medium bg-white/[0.07] text-slate-300 hover:bg-white/10 transition-all"
              >
                Progresión V-IV-I
              </button>
            </div>
          </div>
        </>
      )}

      {/* Visualización común */}
      <div>
        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1.5 block">
          Orientación
        </label>
        <div className="grid grid-cols-2 gap-1">
          <button
            onClick={() => setStringOrder(stringOrder === "physical" ? "tab" : "physical")}
            className="px-2 py-1.5 rounded text-[10px] font-sans font-medium transition-all bg-white/[0.07] text-slate-300 hover:bg-white/10"
          >
            {stringOrder === "physical" ? "Físico ↓" : "Tab ↑"}
          </button>
          <button
            onClick={() => setLeftHanded(!leftHanded)}
            className={`px-2 py-1.5 rounded text-[10px] font-sans font-medium transition-all ${
              leftHanded ? "bg-[var(--bordona-accent)] text-white" : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
            }`}
          >
            {leftHanded ? "Zurdo" : "Diestro"}
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Inspector ───
  const inspector = (
    <div className="space-y-4">
      {viewMode === "octavas" ? (
        <>
          <div className="p-3 rounded-lg bg-white/[0.07] border border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1">
              Nota seleccionada
            </div>
            <div className="text-3xl font-serif font-bold text-[var(--bordona-accent)]">{noteLabel}</div>
            <div className="text-xs text-slate-400 font-sans mt-1">
              {allPositions.length} posiciones en el mástil
            </div>
          </div>

          {showRoute && route && route.steps.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
                Ruta del profesor
              </div>
              <div className="space-y-2">
                <div className="p-2.5 rounded-lg border-l-4 bg-white/[0.08] border border-white/10" style={{ borderLeftColor: PEDAGOGICAL_DEGREE_HEX[0] }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-sans font-semibold text-slate-100">Inicio</span>
                    <span className="text-[10px] text-slate-400 font-sans">Cuerda {route.anchor.string}, t{route.anchor.fret}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-sans mt-0.5">{getOctaveLabel(route.anchor.midi, route.anchor.note)}</div>
                </div>
                {route.steps.map((step, i) => (
                  <div key={i} className="p-2.5 rounded-lg border-l-4 bg-white/[0.08] border border-white/10" style={{ borderLeftColor: ROUTE_COLORS[i % ROUTE_COLORS.length] }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-sans font-semibold text-slate-100">{step.arrow} {step.label}</span>
                      <span className="text-[10px] text-slate-400 font-sans">C{step.to.string} t{step.to.fret}</span>
                    </div>
                    <div className="text-[10px] text-slate-400 font-sans mt-0.5">{getOctaveLabel(step.to.midi, step.to.note)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">Colores</div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[var(--bordona-accent)] inline-block" /><span className="text-xs text-slate-300 font-sans">Anclaje</span></div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[var(--bordona-accent)] opacity-70 inline-block" /><span className="text-xs text-slate-300 font-sans">Ruta</span></div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#F59E0B] inline-block" /><span className="text-xs text-slate-300 font-sans">Otras posiciones</span></div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="p-3 rounded-lg bg-white/[0.07] border border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1">
              Escala Mayor de {noteToSpanish(tonic)}
            </div>
            <div className="text-2xl font-serif font-bold text-[var(--bordona-accent)]">
              {degreeData.map((d) => d.noteSpanish).join(" — ")}
            </div>
            <div className="text-xs text-slate-400 font-sans mt-1">
              7 notas · {selectedDegrees.length} seleccionadas
            </div>
          </div>

          {/* Grados seleccionados */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
              Grados activos
            </div>
            <div className="space-y-1.5">
              {degreeData
                .filter((d) => selectedDegrees.includes(d.num))
                .map((d) => (
                  <div key={d.num} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.08] border border-white/10">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                    <div className="flex-1">
                      <div className="text-xs font-sans font-semibold text-slate-100">{d.roman} — {d.name}</div>
                      <div className="text-[10px] text-slate-400 font-sans">{d.noteSpanish}</div>
                    </div>
                    <span className="text-[10px] text-slate-400 font-sans">+{d.interval} semitonos</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Acordes formados */}
          {selectedDegrees.length >= 3 && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-400/25">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-200/90 font-sans mb-1">
                💡 Acorde detectado
              </div>
              <div className="text-sm font-sans font-bold text-amber-100">
                {getTriadName(selectedDegrees) || "Selección no forma un acorde tríada estándar"}
              </div>
              {getTriadName(selectedDegrees) && (
                <div className="text-[10px] text-amber-200/70 font-sans mt-1">
                  Formado por los grados: {selectedDegrees.sort((a, b) => a - b).join(" - ")}
                </div>
              )}
            </div>
          )}

          {/* Leyenda de grados */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
              Leyenda de grados
            </div>
            <div className="space-y-1">
              {DEGREE_INFO.map((d) => (
                <div key={d.num} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                  <span className="text-[10px] text-slate-300 font-sans">{d.roman} = {d.name}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // ─── Bottom ───
  const bottom = (
    <div className="flex items-center gap-4 h-full px-2">
      {/* Play / Stop */}
      <button
        onClick={() => {
          if (isPlaying) {
            setIsPlaying(false);
            setActiveNote(null);
            /* practice loop se detiene vía isPlaying = false */
          } else {
            setIsPlaying(true);
            setCurrentSeqIndex(0);
          }
        }}
        className={`px-3 h-7 rounded-md text-xs font-sans font-medium transition-all shrink-0 ${
          isPlaying ? "bg-[#2C2825] text-white" : "bg-[var(--bordona-accent)] text-white"
        }`}
      >
        {isPlaying ? "⏹ Detener" : "▶ Recorrer"}
      </button>

      {isPlaying && sequence.length > 0 && (
        <span className="text-xs font-sans text-[var(--bordona-accent)] font-bold truncate shrink-0">
          {currentSeqIndex + 1}/{sequence.length}: {sequence[currentSeqIndex]?.note}
        </span>
      )}

      {practiceMode === "practice" && isPlaying && (
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-16 bg-white/15 rounded-full h-1.5 overflow-hidden">
            <div className="bg-[var(--bordona-accent)] h-full rounded-full transition-all" style={{ width: `${Math.min(engineState.volume * 100, 100)}%` }} />
          </div>
          <span className="text-[10px] font-sans text-slate-400">
            {engineState.isAnalyzing ? "Escuchando..." : "Listo"}
          </span>
        </div>
      )}

      <div className="w-px h-6 bg-white/15 shrink-0" />

      {viewMode === "octavas" ? (
        <>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[var(--bordona-accent)] inline-block" />
              <span className="text-[10px] text-slate-300 font-sans">Anclaje</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B] inline-block" />
              <span className="text-[10px] text-slate-300 font-sans">Posiciones</span>
            </div>
          </div>
          <div className="w-px h-6 bg-white/15 shrink-0" />
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans">Modo</span>
            <span className="px-2 py-0.5 rounded text-xs font-sans font-bold bg-white/[0.07] text-slate-300">
              {showRoute ? "Ruta mnemotécnica" : "Todas las posiciones"}
            </span>
          </div>
          <div className="flex-1" />
          <p className="text-[10px] text-slate-400 font-sans shrink-0">
            {allPositions.length} posiciones · {noteLabel}
          </p>
        </>
      ) : (
        <>
          <div className="flex items-center gap-3 shrink-0">
            {DEGREE_INFO.filter((d) => selectedDegrees.includes(d.num)).map((d) => (
              <div key={d.num} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: d.color }} />
                <span className="text-[10px] text-slate-300 font-sans">{d.roman}</span>
              </div>
            ))}
          </div>
          <div className="w-px h-6 bg-white/15 shrink-0" />
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans">Modo</span>
            <span className="px-2 py-0.5 rounded text-xs font-sans font-bold bg-white/[0.07] text-slate-300">
              Grados de escala
            </span>
          </div>
          <div className="flex-1" />
          <p className="text-[10px] text-slate-400 font-sans shrink-0">
            {selectedDegrees.length} grados · {noteToSpanish(tonic)} Mayor
          </p>
        </>
      )}
    </div>
  );

  // ─── Render ───
  const neckPositions = viewMode === "octavas" ? octavaNeckPositions : degreeNeckPositions;
  const connections = viewMode === "octavas" ? octavaConnections : degreeConnections;

  // En modo práctica, resaltar también la secuencia activa
  const highlightedWithActive = useMemo(() => {
    if (!isPlaying || !activeNote) return neckPositions;
    return neckPositions.map((p) => ({
      ...p,
      highlight: (p.string === activeNote.string && p.fret === activeNote.fret ? "active" : p.highlight) as NeckPosition["highlight"],
    }));
  }, [neckPositions, isPlaying, activeNote]);

  return (
    <LearnWorkbenchShell
      sidebar={sidebar}
      inspector={hideInspector ? undefined : inspector}
      bottom={bottom}
      title={
        viewMode === "octavas"
          ? `${noteLabel} — ${showRoute ? "Ruta mnemotécnica" : "Todas las posiciones"}`
          : `${noteToSpanish(tonic)} Mayor — ${selectedDegrees.length} grados`
      }
      activeModule="notas"
    >
      <div className="flex-1 flex items-center justify-center p-2 min-h-0">
        <div className="w-full h-full max-w-6xl">
          <PracticeSurface
            highlightedPositions={highlightedWithActive}
            activeNote={activeNote}
            showNoteNames={true}
            showDegrees={viewMode === "grados"}
            interactive={true}
            stringOrder={stringOrder}
            leftHanded={leftHanded}
            connections={connections}
            onFretClick={handleFretClick}
            visualStyle="lutherie"
          />
        </div>
      </div>
    </LearnWorkbenchShell>
  );
};

export default NotasExplorerPage;
